import React, { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";
import SlidePanel from "./SlidePanel";
import UserLink from "./UserLink";
import { FIELD_LABELS, showValue } from "./PendingEdits";

const KINDS = [
  { key: "all", label: "Everything" },
  { key: "added", label: "New wrecks" },
  { key: "edit", label: "Edits" },
];

const ago = (ts) => {
  const s = Math.max(0, Math.floor(Date.now() / 1000) - ts);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  return new Date(ts * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};
const fullDate = (ts) => new Date(ts * 1000).toLocaleString();

const VERB = {
  added: { icon: "➕", text: "New wreck added" },
  edit: { icon: "✎", text: "Edited" },
  revert: { icon: "↩", text: "Reverted" },
};

function Entry({ item, ship, isAdmin, busy, onRevert, onViewShip, onOpenUser, onShowHistory, showHistoryLink }) {
  const [open, setOpen] = useState(false);
  const verb = VERB[item.kind] || VERB.edit;
  const fields = item.changes ? Object.keys(item.changes) : [];
  const hasDetail = fields.some((k) => "from" in item.changes[k] || "to" in item.changes[k]);

  return (
    <li className={`chg chg-${item.kind}${item.reverted ? " chg-reverted" : ""}`}>
      <span className="chg-icon" aria-hidden="true">{verb.icon}</span>
      <div className="chg-main">
        <p className="chg-line">
          <strong>{verb.text}</strong>
          {" · "}
          {ship ? (
            <button className="panel-link chg-ship" onClick={() => onViewShip(ship)}>{item.ship_title || `Ship #${item.ship_id}`}</button>
          ) : (
            <span className="chg-ship-gone" title="Not on the map right now">{item.ship_title || `Ship #${item.ship_id}`}</span>
          )}
          {item.reverted && <span className="status-badge rejected chg-badge">Reverted</span>}
        </p>

        <p className="chg-meta">
          {item.actor && <>by <UserLink username={item.actor} onOpen={onOpenUser} /></>}
          {item.reviewer && item.reviewer !== item.actor && (
            <> · {item.kind === "revert" ? "reverted" : "approved"} by <UserLink username={item.reviewer} onOpen={onOpenUser} /></>
          )}
          {" · "}
          <time dateTime={new Date(item.ts * 1000).toISOString()} title={fullDate(item.ts)}>{ago(item.ts)}</time>
        </p>

        {fields.length > 0 && (
          <p className="chg-fields">
            {item.kind === "revert" ? "Restored: " : "Changed: "}
            {fields.map((k) => FIELD_LABELS[k] || k).join(", ")}
            {hasDetail && (
              <button className="panel-link chg-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
                {open ? "Hide details" : "Show details"}
              </button>
            )}
          </p>
        )}

        {open && hasDetail && (
          <div className="diff-list chg-diff">
            {fields.map((k) => (
              <div key={k} className="diff-row">
                <span className="diff-field">{FIELD_LABELS[k] || k}</span>
                <pre className="diff-old">{showValue(item.changes[k].from)}</pre>
                <pre className="diff-new">{showValue(item.changes[k].to)}</pre>
              </div>
            ))}
          </div>
        )}

        <div className="chg-actions">
          {showHistoryLink && (
            <button className="panel-link" onClick={() => onShowHistory(item.ship_id)}>History of this wreck</button>
          )}
          {isAdmin && item.revertable && (
            <button className="panel-link danger" disabled={busy} onClick={() => onRevert(item)}>
              {busy ? "Reverting…" : item.kind === "added" ? "↩ Take off map" : "↩ Revert"}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

export default function RecentChanges({
  API_BASE, account, ships, shipId, onClearShip, onShowHistory,
  onClose, onViewShip, onOpenUser, onShare, onReverted, setMessage,
}) {
  const [kind, setKind] = useState("all");
  const [items, setItems] = useState(null);
  const [next, setNext] = useState(null);
  const [setup, setSetup] = useState(true);
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [version, setVersion] = useState(0); // bump to reload from the top

  const fetchPage = useCallback(async (cursor) => {
    const q = new URLSearchParams({ limit: "30" });
    if (cursor) q.set("before", cursor);
    if (shipId) q.set("ship", shipId);
    if (!shipId && kind !== "all") q.set("kind", kind);
    const res = await fetch(`${API_BASE}/api/changes?${q}`, { cache: "no-store" });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data;
  }, [API_BASE, shipId, kind]);

  useEffect(() => {
    let cancelled = false;
    setItems(null); setError(""); setNext(null);
    fetchPage()
      .then((d) => { if (cancelled) return; setItems(d.items); setNext(d.next); setSetup(d.setup !== false); })
      .catch((e) => { if (!cancelled) setError(e.message || "Couldn't load recent changes."); });
    return () => { cancelled = true; };
  }, [fetchPage, version]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const d = await fetchPage(next);
      setItems((cur) => [...(cur || []), ...d.items]);
      setNext(d.next);
    } catch (e) {
      setError(e.message || "Couldn't load more.");
    } finally {
      setLoadingMore(false);
    }
  };

  const revert = async (item) => {
    const what = item.kind === "added"
      ? `take “${item.ship_title}” off the map`
      : `undo this edit to “${item.ship_title}”`;
    if (!window.confirm(`Are you sure you want to ${what}?\n\nNothing is deleted: the change stays in this list, marked as reverted.`)) return;
    setBusyId(item.id);
    try {
      const res = await fetch(`${API_BASE}/api/changes/revert/${item.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session: Cookies.get("session") }),
      });
      const data = await res.json();
      setMessage(data.error || data.message);
      if (!data.error) { onReverted(); setVersion((v) => v + 1); }
    } catch {
      setMessage("Revert failed.");
    } finally {
      setBusyId(null);
    }
  };

  const shipById = (id) => ships.find((s) => Number(s.id) === Number(id));
  const filterShip = shipId ? shipById(shipId) : null;

  return (
    <SlidePanel
      title={shipId ? "Wreck history" : "Recent changes"}
      subtitle={shipId ? (filterShip?.title || `Ship #${shipId}`) : "New wrecks and approved edits"}
      onClose={onClose}
      wide
      actions={
        <>
          <button className="panel-icon-btn" onClick={() => onShare(shipId)} title="Copy link to this page" aria-label="Copy link">🔗</button>
          <button className="panel-icon-btn" onClick={() => setVersion((v) => v + 1)} title="Refresh" aria-label="Refresh">↻</button>
        </>
      }
    >
      {shipId ? (
        <button className="panel-link" onClick={onClearShip}>← All recent changes</button>
      ) : (
        <div className="panel-tabs view-switch" role="tablist">
          {KINDS.map((k) => (
            <button key={k.key} role="tab" aria-selected={kind === k.key} className={`panel-tab${kind === k.key ? " active" : ""}`} onClick={() => setKind(k.key)}>
              {k.label}
            </button>
          ))}
        </div>
      )}

      {error && <p className="panel-error">{error}</p>}
      {!items && !error && <p className="panel-muted">Loading…</p>}

      {items && !setup && (
        <div className="panel-empty">
          <div className="panel-empty-icon">🛠</div>
          <p>The change log isn't set up yet.</p>
          <p className="panel-muted">An admin needs to run database migration 0004 (see CLOUDFLARE.md).</p>
        </div>
      )}

      {items && setup && items.length === 0 && (
        <div className="panel-empty">
          <div className="panel-empty-icon">📜</div>
          <p>Nothing here yet.</p>
          <p className="panel-muted">New wrecks and edits show up here once an admin approves them.</p>
        </div>
      )}

      {items && items.length > 0 && (
        <>
          <ul className="chg-list">
            {items.map((it) => (
              <Entry
                key={it.id}
                item={it}
                ship={shipById(it.ship_id)}
                isAdmin={!!account?.admin}
                busy={busyId === it.id}
                onRevert={revert}
                onViewShip={onViewShip}
                onOpenUser={onOpenUser}
                onShowHistory={onShowHistory}
                showHistoryLink={!shipId}
              />
            ))}
          </ul>
          {next && (
            <button className="panel-secondary" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Loading…" : "Load older changes"}
            </button>
          )}
        </>
      )}
    </SlidePanel>
  );
}
